import { usePartialState } from '@hooks/usePartialState';
import { Modal } from '@tencent/tea-component';
import { addClassName } from '@tencent/web-base';
import { getCosUrl } from '@utils/upload-cos';
import classNames from 'classnames';
import { CSSProperties, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import close_icon from './imgs/close_icon.svg';
import styles from './Preview.module.scss';

type ImagePreviewProps = {
  cosId?: string;
  url?: string;
  className?: string;
  style?: CSSProperties;
};

export interface ImagePreviewRef {
  /**
   * 替换tea组件的点击图片预览功能
   */
  patchElement: (element: HTMLDivElement) => void;
  /**
   * 打开预览
   */
  open: () => void;
  /**
   * 关闭预览
   */
  close: () => void;
}

/**
 * 图片预览组件
 *
 * @example
 * src/pages/event/[eventid]/members/guest/components/GuestAvatarUpload/index.module.scss
 * src/pages/event/[eventid]/members/guest/components/GuestAvatarUpload/index.tsx
 */
const Preview = forwardRef<ImagePreviewRef, ImagePreviewProps>((props, ref) => {
  const { className, style, url, cosId } = props;
  const [previewUrl, setPreviewUrl] = useState('');
  const [modalState, setModalState] = usePartialState({
    visible: false,
  });
  const domRef = useRef<HTMLDivElement | null>(null);

  const patchElement = (el: HTMLDivElement) => {
    registerEvent(el);
  };

  const open = () => {
    setModalState({ visible: true });
  };

  const close = () => {
    setModalState({ visible: false });
  };

  useImperativeHandle(ref, () => ({
    patchElement,
    open,
    close,
  }));

  const registerEvent = (el: HTMLDivElement) => {
    if (domRef.current) {
      domRef.current.removeEventListener('click', handleClick);
    }
    const div = el.querySelector<HTMLDivElement>('div.tea-form-upload__thumb');
    if (div) {
      div.addEventListener('click', handleClick, false);
      domRef.current = div;
    }
    const img = el.querySelector<HTMLImageElement>('img.tea-img__preview');
    if (img) {
      addClassName(img, 'disable-click');
    }
  };

  const handleClick = () => {
    if (url) {
      setModalState({ visible: true });
    }
  };

  useEffect(
    () => () => {
      if (domRef.current) {
        domRef.current.removeEventListener('click', handleClick);
      }
    },
    [],
  );

  useEffect(() => {
    (async () => {
      let href = url;
      if (cosId) {
        href = await getCosUrl(cosId, '3').catch(() => url);
      }
      if (href) {
        setPreviewUrl(href);
      }
    })();
  }, [cosId, url]);

  return (
    <Modal
      className={classNames(styles.preview, className)}
      style={style}
      visible={modalState.visible}
      disableCloseIcon
    >
      <Modal.Body>
        <div className={styles.content}>
          <div className={styles.flexBox}>
            <img className={styles.maxImg} src={previewUrl} alt="" draggable={false} />
            <div className={styles.closeBtn} onClick={close}>
              <img className={styles.img} src={close_icon} alt="" draggable={false} />
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
});

Preview.displayName = 'Preview';

export default Preview;
